import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MoveUpRight, Play, Plus } from "lucide-react";
import { CASE_STUDIES, IMAGES, SERVICES } from "../data/content";
import { MaskedLines, Reveal, EASE } from "../components/Motion";
import Marquee from "../components/Marquee";
import { CtaBand } from "../components/Cta";

const BENTO_SIZES = ["wide", "tall", "standard", "standard"];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const scrollToWork = () => {
    const el = document.getElementById("home-work");
    if (window.__lenis && el) window.__lenis.scrollTo(el, { offset: -40 });
    else el?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="hero" ref={ref} data-testid="hero-section">
      <motion.div className="hero-image" style={{ y: bgY }} />
      <div className="hero-overlay" />
      <motion.div className="hero-content" style={{ opacity: fade }}>
        <motion.p className="eyebrow" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.9 }}>
          <span className="pulse-dot" /> Product branding &amp; packaging studio · Pune, India
        </motion.p>
        <h1>
          <MaskedLines lines={[<>Make the</>, <em key="u">unforgettable.</em>]} delay={0.35} />
        </h1>
        <motion.div
          className="hero-bottom"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9, ease: EASE }}
        >
          <p>A product branding and packaging design studio in Pune — crafting films, identities and digital experiences for brands with something to say.</p>
        </motion.div>
      </motion.div>
      <motion.div
        className="hero-ring"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.15, duration: 1, ease: EASE }}
      >
        <svg viewBox="0 0 200 200" aria-hidden="true">
          <defs>
            <path id="ringPath" d="M100,100 m-80,0 a80,80 0 1,1 160,0 a80,80 0 1,1 -160,0" />
          </defs>
          <text>
            <textPath href="#ringPath">OASIS BUSINESS SOLUTIONS · MAKE THE UNFORGETTABLE · </textPath>
          </text>
        </svg>
        <button className="circle-play" onClick={scrollToWork} data-testid="hero-view-work-button" aria-label="Scroll to selected work">
          <Play size={16} fill="currentColor" />
        </button>
      </motion.div>
      <div className="scroll-cue">
        Scroll to explore <span>↓</span>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee items={["Film", "Identity", "Product", "Social", "Web", "Motion"]} testid="home-marquee" />

      <section className="manifesto section-pad" data-testid="manifesto-section">
        <Reveal>
          <p className="eyebrow">Our point of view</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2>
            Good work gets seen.
            <br />
            <span>Great work stays with you.</span>
          </h2>
          <p className="manifesto-copy">
            Oasis Business Solutions is a product branding and media production studio in Pune, working with ambitious food, FMCG
            and D2C brands across India. From first spark to final frame, we turn ideas into worlds that hold attention.
          </p>
        </Reveal>
      </section>

      <section className="services section-pad" data-testid="services-section">
        <Reveal className="section-heading">
          <p className="eyebrow">What we do</p>
          <Link to="/services" className="text-link" data-testid="home-services-link">
            All services <ArrowUpRight size={16} />
          </Link>
        </Reveal>
        <div className="service-list">
          {SERVICES.map((service, index) => (
            <Reveal key={service.slug} delay={index * 0.06}>
              <Link to={`/services/${service.slug}`} className="service-row" data-testid={`home-service-${service.slug}`}>
                <span className="service-number">0{index + 1}</span>
                <span>{service.name}</span>
                <MoveUpRight size={20} />
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="home-work" className="work section-pad" data-testid="work-section">
        <Reveal className="section-heading">
          <div>
            <p className="eyebrow">Selected work</p>
            <h2>
              Made to be <em>felt.</em>
            </h2>
          </div>
          <Link to="/work" className="text-link" data-testid="home-work-link">
            View all work <ArrowUpRight size={16} />
          </Link>
        </Reveal>
        <div className="project-grid">
          {CASE_STUDIES.map((project, index) => (
            <Reveal key={project.slug} delay={index * 0.08} className={`reveal-card ${BENTO_SIZES[index]}`}>
              <Link to={`/work/${project.slug}`} className={`project-card ${BENTO_SIZES[index]}`} data-testid={`home-project-${project.slug}`}>
                <img src={project.heroImage} alt={project.title} loading="lazy" />
                <div className="project-shade" />
                <div className="project-meta">
                  <span>{project.scope}</span>
                  <span>{project.year}</span>
                </div>
                <h3>{project.title}</h3>
                <span className="project-arrow">
                  <ArrowUpRight />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="logo-wall" data-testid="logo-wall">
        <Reveal>
          <p className="eyebrow">Trusted by ambitious brands</p>
        </Reveal>
        <div className="lw-track">
          {[0, 1].map((copy) => (
            <div className="lw-group" key={copy} aria-hidden={copy === 1}>
              {[
                ["Afterlight", "serif"],
                ["Nocturne FM", "mono"],
                ["Terra Forma", "italic"],
                ["Pulse Athletics", "mono"],
                ["Vantage", "serif"],
                ["Kiln & Co.", "italic"],
                ["Static Club", "mono"],
                ["Northgate", "serif"],
                ["Bloom Box", "italic"],
                ["Aster", "serif"],
              ].map(([name, style]) => (
                <span key={name} className={`lw-item ${style}`}>
                  {name}
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      <section className="process section-pad" data-testid="process-section">
        <Reveal className="section-heading">
          <p className="eyebrow">The process</p>
          <span className="section-count">01 — 03</span>
        </Reveal>
        <div className="process-grid">
          <Reveal>
            <h2>
              Clear thinking.
              <br />
              <em>Beautiful making.</em>
            </h2>
            <p>Every project is different. Our approach stays curious, considered, and close to the work.</p>
          </Reveal>
          <div className="process-steps">
            {[
              ["01", "Find the signal", "We get to the heart of what you're trying to say."],
              ["02", "Build the world", "Strategy, art direction, production — made tangible."],
              ["03", "Make it matter", "The final frame is where the conversation starts."],
            ].map((step, i) => (
              <Reveal key={step[0]} delay={i * 0.08}>
                <div className="process-step">
                  <span>{step[0]}</span>
                  <div>
                    <h3>{step[1]}</h3>
                    <p>{step[2]}</p>
                  </div>
                  <Plus size={18} />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="about section-pad" data-testid="about-section">
        <Reveal className="about-image">
          <img src={IMAGES.studio} alt="Inside the Oasis studio" loading="lazy" />
        </Reveal>
        <Reveal delay={0.12} className="about-copy">
          <p className="eyebrow">A small studio, big energy</p>
          <h2>
            We make room
            <br />
            for the <em>unexpected.</em>
          </h2>
          <p>
            We're a collective of directors, designers, producers, and visual obsessives. Based between the digital and
            physical, we collaborate with ambitious teams to make work with a pulse.
          </p>
          <div className="about-stat">
            <strong>
              10<span>+</span>
            </strong>
            <span>
              years making
              <br />
              things matter
            </span>
          </div>
          <Link to="/about" className="text-link" data-testid="home-about-link">
            More about the studio <ArrowUpRight size={16} />
          </Link>
        </Reveal>
      </section>

      <section className="testimonial section-pad" data-testid="testimonial-section">
        <Reveal>
          <div className="quote-mark">"</div>
          <blockquote>They didn&apos;t just make the film — they found the feeling we couldn&apos;t put into words.</blockquote>
          <p>— Maya Chen, Global Brand Director · Aster</p>
        </Reveal>
      </section>

      <CtaBand testid="home-cta-band" />
    </>
  );
}
